import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "./store";
import { Backdrop, Box, CssBaseline, ThemeProvider } from "@mui/material";
import "@aws-amplify/ui-react/styles.css";
import { useNavigate, useRoutes } from "react-router-dom";
import routes from "./routes";
import theme from "./theme";
import SnackBarToast from "./components/base/SnackbarToast";
import { useLocation } from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react";
import AmplifyAuth from "./components/layouts/AmplifyAuth";
import { fetchAuthSession } from "aws-amplify/auth";
import { fetchDbUser, fetchProfile } from "./slices/authentication";
import { Applications } from "./types/company";
import {
  handleToastState,
  setSavedRoute,
  toggleLicense,
} from "./slices/shared";
import { getJobCount } from "./slices/job";
import { getSimpleUsers } from "./slices/user";
import checkRole, { UserRoles } from "./helpers/checkRole";

interface Props {}

const App: React.FC<Props> = (props) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toastStatus, globalLoadingState, savedRoute, productLicensed } =
    useSelector((state) => state.shared);
  const { loggedInUser, profileUser, userCompany } = useSelector(
    (state) => state.authentication,
  );
  const { jobCounts } = useSelector((state) => state.job);
  const content = useRoutes(
    routes(
      checkRole(loggedInUser!, UserRoles.GmdAdmin) ||
        checkRole(loggedInUser!, UserRoles.GlobalAdmin),
      loggedInUser,
    ),
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const loadProfile = useCallback(async () => {
    if (!profileUser) {
      dispatch(fetchProfile());
    }
  }, [dispatch, profileUser]);

  const loadDbUser = useCallback(async () => {
    if (!!profileUser && !loggedInUser) {
      dispatch(fetchDbUser()).then((result) => {
        if (result.meta.requestStatus === "fulfilled") {
          if (!jobCounts.length) {
            dispatch(getJobCount());
          }
        }
      });
    }
  }, [profileUser, loggedInUser, dispatch, jobCounts.length]);

  const loadLicense = useCallback(async () => {
    if (userCompany && productLicensed === undefined) {
      let companyLicenses = userCompany?.licenses;
      if (companyLicenses?.length! > 0) {
        const gmdLicense = companyLicenses?.find(
          (x) => x.productId === Applications.Gmd,
        );

        if (gmdLicense?.licensed) {
          return dispatch(toggleLicense(true));
        }
      }
      return dispatch(toggleLicense(false));
    }
  }, [dispatch, productLicensed, userCompany]);

  const loadRoute = useCallback(async () => {
    if (savedRoute) {
      const route = savedRoute;
      dispatch(setSavedRoute(undefined));
      navigate(`/${route}`);
    }
  }, [dispatch, navigate, savedRoute]);
  const loadUsers = useCallback(async () => {
    dispatch(getSimpleUsers(true));
  }, [dispatch]);

  const handleLoadApp = useCallback(async () => {
    const session = await fetchAuthSession();

    if (session?.tokens) {
      await Promise.all([
        loadProfile(),
        loadDbUser(),
        loadLicense(),
        loadRoute(),
        loadUsers(),
      ]);
    } else {
      window.location.href = `${process.env.REACT_APP_AUTH_URL}/app/gmd`;
    }
  }, [loadProfile, loadDbUser, loadLicense, loadRoute, loadUsers]);

  const handleCloseToast = () => {
    dispatch(handleToastState({ open: "none", message: "" }));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={{
          zIndex: theme.zIndex.drawer + 2,
          backgroundColor: "rgba(38,84,158,0.5)",
        }}
        open={globalLoadingState.isLoading}
      >
        <Box
          sx={{
            fontSize: "100px",
            position: "absolute",
            overflow: "hidden",
            display: "inline-block",
            animation: "blink 2s infinite linear",
            "@keyframes blink": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.1 },
            },
          }}
        >
          <img height={150} src={"../static/images/SVPro.png"} alt="logo" />
        </Box>
      </Backdrop>
      <SnackBarToast
        open={toastStatus.open !== "none"}
        message={toastStatus.message}
        type={toastStatus.open !== "none" ? toastStatus.open : "info"}
        handleClose={handleCloseToast}
      />
      <Authenticator.Provider>
        <AmplifyAuth loadApp={handleLoadApp}>{content}</AmplifyAuth>
      </Authenticator.Provider>
    </ThemeProvider>
  );
};

export default App;
